// Launch parameters of the Android app (com.gaintplay.makemoney.android, a Trusted Web Activity).
// The current app (gaintplay_new_twa, LauncherActivity.launchIntent) opens
//   https://gaintplay.com/?utm_source=twa&device=…&brand=…&model=…&gaid=…&device_timezone=…
//     &device_sim_provider=…&device_country=…&device_language=…&device_locale_country=…
//     &device_board=…&device_id=<Android ID>[&fcm_token=…]&app_version=…&apps_flyer_id=…&key=<md5>
// and since build 32 (docs/ANDROID_APP.md §4) also &app_build=…&bridge=…&install_id=…&gp=<base64url
// JSON of device signals> before key.
// built with Android's Uri.Builder: values Uri.encode'd (a space is %20), and a missing value
// written as the literal "null". Older installs sent one device_details string instead
// ("Device Language : en, Device Country : np, …", gaintplay/src/helpers/userTracking/TwaData),
// with the Android ID and the device checks (rooted, emulator, …) in it. Both are read.
//
// `key` is MD5 of the URL as the app built it, before "&key=" was appended: the base
// "https://gaintplay.com/?" (hard-coded in the app) and the query without key. It is checked against
// the query exactly as received, and also rebuilt with encodeURIComponent (the old site's check;
// the same characters as Uri.encode) and unencoded (the old sample URL only matches that way). There
// is no secret in it: a tamper and truncation hint, never a security boundary.
//
// Pure functions, no imports beyond MD5, so apps/web's launch script (its own tiny bundle) can use
// them: see apps/web/src/launch.ts.
import { md5Hex } from './providers/md5.js';

export const APP_PACKAGE = 'com.gaintplay.makemoney.android';

/** What offer walls get when the user's advertising ID is unknown (as on the old site). */
export const ZERO_GAID = '00000000-0000-0000-0000-000000000000';

/** Bases the app has signed launch URLs with: the old site, and this one. */
export const LAUNCH_KEY_BASES = ['https://gaintplay.com/?', 'https://app.gaintplay.com/?'];

/** What we keep from a launch. Nothing else from device_details is read. */
export interface AppLaunchData {
  brand?: string;
  model?: string;
  /** Android's device (product) name, e.g. "m31". */
  device?: string;
  appVersion?: string;
  gaid?: string;
  /**
   * The device_id parameter: the Android ID in the current app (or an MD5 of build properties when
   * there is none); a name-based UUID of it in older installs. Treated like the Android ID.
   */
  installId?: string;
  androidId?: string;
  appsflyerId?: string;
  fcmToken?: string;
  /** The app's versionCode (build 32 and later). */
  appBuild?: string;
  /** Which site → app commands the app handles (docs/ANDROID_APP.md §6); 2 = notification, verify… */
  bridge?: string;
  /** A random id the app makes on first run (build 32 and later), unlike installId. */
  appInstallId?: string;
  /** The notification permission when the app launched, from gp.notif: granted, denied or blocked. */
  notif?: string;
  language?: string;
  country?: string;
  localeCountry?: string;
  timezone?: string;
  simProvider?: string;
  playStore?: boolean;
  developerMode?: boolean;
  rooted?: boolean;
  emulator?: boolean;
  tablet?: boolean;
  wifiOnly?: boolean;
  cloned?: boolean;
  secondaryUser?: boolean;
}

type StringField = {
  [K in keyof AppLaunchData]-?: AppLaunchData[K] extends string | undefined ? K : never;
}[keyof AppLaunchData];
type BoolField = Exclude<keyof AppLaunchData, StringField>;

/** Top-level launch parameters → field. */
const TOP: Record<string, StringField> = {
  brand: 'brand',
  model: 'model',
  device: 'device',
  app_version: 'appVersion',
  gaid: 'gaid',
  device_id: 'installId',
  apps_flyer_id: 'appsflyerId',
  fcm_token: 'fcmToken',
  app_build: 'appBuild',
  bridge: 'bridge',
  install_id: 'appInstallId',
};

/** device_details entries (normalised names, see detailKey) → field. */
const DETAIL_STRINGS: Record<string, StringField> = {
  device_language: 'language',
  device_country: 'country',
  device_locale_country: 'localeCountry',
  device_timezone: 'timezone',
  device_sim_provider: 'simProvider',
  device_android_id: 'androidId',
};
const DETAIL_BOOLS: Record<string, BoolField> = {
  is_app_installed_from_play_store: 'playStore',
  is_developer_mode_enabled: 'developerMode',
  is_device_rooted: 'rooted',
  is_device_emulator: 'emulator',
  is_device_tablet: 'tablet',
  is_device_wifi_only: 'wifiOnly',
  is_app_cloned: 'cloned',
  is_app_on_secondary_user: 'secondaryUser',
};

/** Longest value kept from any launch parameter; the API validates each field properly. */
const MAX_VALUE = 512;

/** "Device Sim provider" → "device_sim_provider"; "is App cloned" → "is_app_cloned". */
export function detailKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * The app's "Name : value, Name : value, …" list, as normalised name → value. Values are split at
 * the first colon only, so "GMT+05:45" and build strings keep theirs.
 */
export function parseDeviceDetails(details: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of details.split(',')) {
    const at = part.indexOf(':');
    if (at < 1) continue;
    const key = detailKey(part.slice(0, at));
    if (key && !(key in out)) out[key] = part.slice(at + 1).trim();
  }
  return out;
}

/** Android's Uri.Builder writes a missing value as "null"; some builds send other placeholders. */
const MISSING = new Set(['', 'null', 'undefined', 'unknown']);
const present = (v: string | undefined | null): string | undefined => {
  const t = v?.trim();
  return t && !MISSING.has(t.toLowerCase()) ? t : undefined;
};

const bool = (v: string | undefined): boolean | undefined => {
  const t = v?.trim().toLowerCase();
  return t === 'true' ? true : t === 'false' ? false : undefined;
};

/** Is this a launch by the Android app? (The old site's test: utm_source=twa.) */
export function isAppLaunch(params: URLSearchParams): boolean {
  return (params.get('utm_source') ?? '').toLowerCase() === 'twa';
}

/** A parameter that is part of the app's launch data, and so leaves the address bar. */
export function isLaunchParam(name: string, value?: string): boolean {
  if (name === 'utm_source') return (value ?? '').toLowerCase() === 'twa';
  if (name === 'key' || name === 'device_details' || name === 'gp' || name in TOP) return true;
  const k = detailKey(name);
  return k.startsWith('device_') || k.startsWith('is_');
}

/** The query string without the launch parameters ("" or "?a=b"). */
export function stripLaunchParams(search: string): string {
  // Rebuilt rather than delete(name, value), which older Chrome versions in the app lack.
  const kept = [...new URLSearchParams(search)].filter(([k, v]) => !isLaunchParam(k, v));
  const s = new URLSearchParams(kept).toString();
  return s ? `?${s}` : '';
}

/** The query string as received ("?a=1&key=…&b=2"), without its key parameter and the "?". */
export function queryWithoutKey(search: string): string {
  return search
    .replace(/^\?/, '')
    .split('&')
    .filter((part) => {
      const name = part.split('=')[0]!;
      let decoded = name;
      try {
        decoded = decodeURIComponent(name.replace(/\+/g, ' '));
      } catch {
        /* keep the raw name */
      }
      return part !== '' && decoded !== 'key';
    })
    .join('&');
}

/**
 * The strings the app may have hashed into `key`, for each base: the query exactly as received
 * (when `rawSearch` is given: what the current app hashes), and every parameter except key in
 * order with values encodeURIComponent'd (the old site's check) or as sent (the old sample).
 */
export function launchKeyMessages(
  params: URLSearchParams,
  bases: string[],
  rawSearch?: string,
): string[] {
  const pairs = [...params].filter(([k]) => k !== 'key');
  const forms = [
    ...(rawSearch !== undefined ? [queryWithoutKey(rawSearch)] : []),
    pairs.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&'),
    pairs.map(([k, v]) => `${k}=${v}`).join('&'),
  ];
  const out = new Set<string>();
  for (const b of bases) for (const f of forms) out.add(b + f);
  return [...out];
}

/** Does `key` match the rest of the launch URL under one of `bases`? */
export function verifyLaunchKey(
  params: URLSearchParams,
  bases: string[],
  rawSearch?: string,
): boolean {
  const key = (params.get('key') ?? '').trim().toLowerCase();
  if (!/^[0-9a-f]{32}$/.test(key)) return false;
  return launchKeyMessages(params, bases, rawSearch).some((m) => md5Hex(m) === key);
}

const NOTIF = new Set(['granted', 'denied', 'blocked']);

/** gp.notif from the base64url JSON device signals (§5); anything malformed reads as unknown. */
export function launchNotif(gp: string | null): string | undefined {
  if (!gp || gp.length > 4096 || !/^[A-Za-z0-9_-]+$/.test(gp)) return undefined;
  try {
    const b64 = gp.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    const json = new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
    const notif = (JSON.parse(json) as { notif?: unknown } | null)?.notif;
    return typeof notif === 'string' && NOTIF.has(notif) ? notif : undefined;
  } catch {
    return undefined;
  }
}

/** The fields we keep from a launch URL; unknown and unwanted parameters are dropped. */
export function extractLaunchData(params: URLSearchParams): AppLaunchData {
  const out: AppLaunchData = {};
  const setStr = (f: StringField, v: string | undefined) => {
    const t = present(v);
    if (t && out[f] === undefined) out[f] = t.slice(0, MAX_VALUE);
  };
  const setBool = (f: BoolField, v: string | undefined) => {
    const b = bool(present(v));
    if (b !== undefined && out[f] === undefined) out[f] = b;
  };
  for (const [name, field] of Object.entries(TOP)) setStr(field, params.get(name) ?? undefined);
  // Separate parameters (the current app) first, then the combined device_details string (older
  // installs). A "null" placeholder doesn't hide a real value sent the other way.
  const details: Record<string, string> = {};
  for (const [k, v] of params) {
    const key = detailKey(k);
    if ((key in DETAIL_STRINGS || key in DETAIL_BOOLS) && !(key in details) && present(v))
      details[key] = v;
  }
  const combined = params.get('device_details');
  if (combined)
    for (const [k, v] of Object.entries(parseDeviceDetails(combined)))
      if (!(k in details)) details[k] = v;
  for (const [k, v] of Object.entries(details)) {
    if (k in DETAIL_STRINGS) setStr(DETAIL_STRINGS[k]!, v);
    else if (k in DETAIL_BOOLS) setBool(DETAIL_BOOLS[k]!, v);
  }
  const notif = launchNotif(params.get('gp'));
  if (notif) out.notif = notif;
  return out;
}
